import React, { FC, useCallback, useState } from "react";
import { AxiosError } from "axios";
import { Tooltip } from "@mui/material";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { useAxios } from "myAxios";
import styles from "./index.module.scss";
import { Cookies } from "utils";
import { FIRST_NAME_COOKIE, LAST_NAME_COOKIE } from "constant";
interface GeneratePptSuccessResult {
  blob: Blob;
  fileName: string;
}
interface CustomChildProps {
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLElement>;
}

export interface MarkdownToPPTBtnProps {
  material: string;
  template?: string;
  endpoint?: string;
  buttonText?: string;
  tooltipTitle?: React.ReactNode;
  className?: string;
  children?: React.JSX.Element | ((props: { loading: boolean }) => React.JSX.Element);
  loadingChildren?: React.JSX.Element | ((props: { loading: boolean }) => React.JSX.Element);
  onSuccess?: (response: GeneratePptSuccessResult) => void;
  onError?: (error: unknown) => void;
}

const DEFAULT_TEMPLATE = "template_cisco_webex"; // template_mber
const DEFAULT_FILE_NAME = "generated-ppt.pptx";
const PPT_MIME_TYPE = "application/vnd.openxmlformats-officedocument.presentationml.presentation";

const stripFileNameQuotes = (fileName: string) => fileName.trim().replace(/^"|"$/g, "");

const decodeHeaderFileName = (fileName: string) => {
  const normalizedFileName = stripFileNameQuotes(fileName).replace(/^UTF-8''/i, "");

  try {
    return decodeURIComponent(normalizedFileName);
  } catch {
    return normalizedFileName;
  }
};

const parseContentDispositionFileName = (contentDisposition?: string) => {
  if (!contentDisposition) return undefined;

  const utf8Match = contentDisposition.match(/(?:^|;)\s*filename\*\s*=\s*(?:"([^"]*)"|([^;]*))/i);
  const utf8FileName = utf8Match?.[1] || utf8Match?.[2];

  if (utf8FileName) {
    return decodeHeaderFileName(utf8FileName);
  }

  const asciiMatch = contentDisposition.match(/(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]*))/i);
  const asciiFileName = asciiMatch?.[1] || asciiMatch?.[2];

  if (asciiFileName) {
    return stripFileNameQuotes(asciiFileName);
  }

  return undefined;
};

const getPlainHeaderFileName = (fileName?: string) => {
  const plainFileName = fileName?.split(";")[0];

  return plainFileName ? stripFileNameQuotes(plainFileName) : undefined;
};

const getFileNameFromContentDisposition = (contentDisposition?: string) => {
  return parseContentDispositionFileName(contentDisposition) || DEFAULT_FILE_NAME;
};

const getFileNameFromHeaders = (headers?: Record<string, string>) => {
  const proxiedFileName = headers?.filename;

  if (proxiedFileName) {
    return (
      parseContentDispositionFileName(proxiedFileName) ||
      getPlainHeaderFileName(proxiedFileName) ||
      DEFAULT_FILE_NAME
    );
  }

  return getFileNameFromContentDisposition(headers?.["content-disposition"]);
};

const ensurePptExtension = (fileName: string) => {
  if (/\.pptx$/i.test(fileName)) {
    return fileName;
  }

  return `${fileName}.pptx`;
};

const extractErrorMessage = async (error: unknown) => {
  const defaultMessage = "PPT generation failed, please try again later.";
  const responseData = (error as AxiosError)?.response?.data;

  if (responseData instanceof Blob) {
    const text = await responseData.text();

    if (!text) return defaultMessage;

    try {
      const parsed = JSON.parse(text) as { message?: string };
      return parsed.message || text || defaultMessage;
    } catch {
      return text || defaultMessage;
    }
  }

  if (typeof responseData === "string") {
    return responseData || defaultMessage;
  }

  const errorPayload = responseData as { message?: string } | undefined;
  return errorPayload?.message || defaultMessage;
};

const MarkdownToPPTBtn: FC<MarkdownToPPTBtnProps> = ({
  material,
  template = DEFAULT_TEMPLATE,
  endpoint = "/api/csdigital-blob/ai-assistant/v1/ai/ppt/generate",
  buttonText = "Generate PPT",
  tooltipTitle,
  className,
  children,
  loadingChildren,
  onSuccess,
  onError,
}) => {
  const axios = useAxios({}, "csdigital");
  const [loading, setLoading] = useState(false);
  const currentTooltipTitle = tooltipTitle ?? (loading ? "Generating PPT..." : buttonText);
  const userName = Cookies.get(FIRST_NAME_COOKIE);
  const lastName = Cookies.get(LAST_NAME_COOKIE);

  const renderLoadingChildren = useCallback(() => {
    if (!loadingChildren) return null;

    return loadingChildren instanceof Function ? loadingChildren({ loading }) : loadingChildren;
  }, [loading, loadingChildren]);

  const handleClick = async () => {
    if (!material || !material.trim()) {
      toast.error("Material is empty, cannot generate PPT.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post<Blob>(
        endpoint,
        {
          material,
          template,
          reporter: `${userName} ${lastName}`,
        },
        {
          responseType: "blob",
        }
      );
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type: response.headers?.["content-type"] || PPT_MIME_TYPE,
            });
      const fileName = ensurePptExtension(
        getFileNameFromHeaders(response.headers as Record<string, string>)
      );

      saveAs(blob, fileName);
      toast.success("PPT downloaded.");
      onSuccess?.({ blob, fileName });
    } catch (error) {
      const message = await extractErrorMessage(error);

      toast.error(message);
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  if (children) {
    const customChild =
      loading && loadingChildren
        ? renderLoadingChildren()
        : children instanceof Function
          ? children({ loading })
          : children;

    if (React.isValidElement(customChild)) {
      const childElement = customChild as React.ReactElement<CustomChildProps>;
      const existingStyle = childElement.props.style || {};
      const existingClassName = childElement.props.className || "";
      const existingOnClick = childElement.props.onClick;

      return (
        <Tooltip
          title={currentTooltipTitle}
          arrow
        >
          {React.cloneElement(childElement, {
            className: [existingClassName, className].filter(Boolean).join(" "),
            onClick: (event: React.MouseEvent<HTMLElement>) => {
              existingOnClick?.(event);
              if (!event.defaultPrevented && !loading) {
                void handleClick();
              }
            },
            style: {
              ...existingStyle,
              cursor: loading ? "not-allowed" : existingStyle.cursor,
              opacity: loading ? 0.6 : existingStyle.opacity,
            },
          })}
        </Tooltip>
      );
    }

    return (
      <Tooltip
        title={currentTooltipTitle}
        arrow
      >
        <>{customChild}</>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={currentTooltipTitle}
      arrow
    >
      <span className={styles.tooltipWrapper}>
        <button
          type="button"
          className={[styles.generateBtn, className].filter(Boolean).join(" ")}
          onClick={handleClick}
          disabled={loading}
        >
          {loading ? (
            loadingChildren ? (
              renderLoadingChildren()
            ) : (
              <>
                <span
                  className={styles.spinner}
                  aria-hidden="true"
                />
                <span>Generating...</span>
              </>
            )
          ) : (
            buttonText
          )}
        </button>
      </span>
    </Tooltip>
  );
};

export default MarkdownToPPTBtn;
